import { FC, ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import ArrowRightIcon from '~/icons/arrow-right.svg';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import Skeleton from '@/shared/ui/Skeleton';

interface Props {
  className?: string;
  iconSlot?: ReactNode;
  title: string;
  descr?: string;
  price: string;
  date: string;
  onClick: () => void;
  isLoading?: boolean;
}

export const DeliveryCard: FC<Props> = ({
  className,
  iconSlot,
  title,
  descr,
  price,
  date,
  onClick,
  isLoading = false,
}) => {
  return (
    <div className={twMergeExt(cvaRoot({ isLoading }), className)} onClick={isLoading ? undefined : () => onClick()}>
      <div className={cvaContainer()}>
        <div className={'w-2.4 h-2.4'}>{iconSlot}</div>

        <div className={'flex flex-col gap-0.4 flex-1'}>
          <div className={'flex justify-between items-center'}>
            <div className={'flex gap-0.8'}>
              <div>{title}</div>
              {isLoading ? (
                <Skeleton className={twMergeExt(cvaSkeleton(), 'top-0.4 w-10 h-2')} />
              ) : (
                <div className={'text-bodyL text-cBlack48'}>{descr}</div>
              )}
            </div>
            <ArrowRightIcon className={'w-1.6 h-1.6 text-cBlack'} />
          </div>
          <div>
            <div className={twMergeExt(cvaProperties())}>
              <dl className={cvaProperty()}>
                {isLoading ? (
                  <dt className={cvaKey()}>
                    <Skeleton className={twMergeExt(cvaSkeleton(), 'w-9.4 h-2')} />
                    <span className={cvaKeyWord()} />
                  </dt>
                ) : (
                  <dt
                    className={cvaKey()}
                    dangerouslySetInnerHTML={{
                      __html: price
                        .split(' ')
                        .map((word) => `<span class='${cvaKeyWord()}'>${word}</span>`)
                        .join(' '),
                    }}
                  />
                )}

                <div className={cvaPropertySeparator()} />

                {isLoading ? (
                  <dd className={cvaValue()}>
                    <Skeleton className={twMergeExt(cvaSkeleton(), 'w-7 h-2')} />
                  </dd>
                ) : (
                  <dd
                    className={cvaValue()}
                    dangerouslySetInnerHTML={{
                      __html: date,
                    }}
                  />
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const cvaRoot = cva(
  ['DeliveryCard-cvaRoot', 'relative', 'overflow-hidden', 'rounded-[1.6rem]', 'text-bodyLSemibold', 'bg-cLightGrey'],
  {
    variants: {
      isLoading: {
        true: 'cursor-default',
        false: [
          'cursor-pointer',
          'before:bg-cDarkGreyOnGrey after:bg-cDarkGreyOnGrey',
          'before:pb-[150%] before:absolute before:left-[-10%] before:transform-gpu before:translate-y-[68%] before:scale-0 before:w-[120%] before:h-0 before:rounded-[50%]',
          'hnotdisable:before:translate-y-0 hnotdisable:before:scale-[1.3] hnotdisable:before:transition-transform hnotdisable:before:duration-400 hnotdisable:before:ease-button-before',
          'after:absolute after:top-0 after:left-0 after:transform-gpu after:translate-y-[-100%] after:w-full after:h-full after:transition-transform after:ease-button-after after:duration-500',
          'hnotdisable:after:translate-y-0 hnotdisable:after:[transition-duration:0.05s] hnotdisable:after:[transition-delay:0.4s] hnotdisable:after:transition-transform hnotdisable:after:ease-button-after',
        ],
      },
    },
  }
);

const cvaContainer = cva(['DeliveryCard-cvaContainer', 'relative z-[1]', 'flex gap-1.6', 'p-2.4']);

const cvaSkeleton = cva(['DeliveryCard-cvaSkeleton', 'relative', 'rounded-[0.4rem]', 'bg-cDarkGreyOnGrey']);

const cvaProperties = cva([
  'DeliveryCard-cvaProperties',
  'flex flex-col gap-1.2',
  'overflow-hidden',
  'bg-transparent [&_dt]:text-cMediumGreen [&_dd]:text-cMediumGreen',
]);

const cvaProperty = cva(['DeliveryCard-cvaProperty', 'relative', 'flex justify-between items-end', 'bg-inherit']);

const cvaKey = cva(['DeliveryCard-cvaKey', 'text-cBlack48 bg-inherit']);

const cvaPropertySeparator = cva([
  'DeliveryCard-cvaPropertySeparator',
  'relative',
  'flex-1',
  'before:absolute before:left-0 before:w-full before:bottom-0.4 before:h-0.1',
  'before:[background-image:linear-gradient(90deg,rgba(0,0,0,0.1)_50%,hsla(0,0%,100%,0)_0)] before:[background-size:0.4rem_0.1rem]',
]);

const cvaValue = cva(['DeliveryCard-cvaValue', 'relative pl-0.4 text-cBlack bg-inherit', 'text-right']);

const cvaKeyWord = cva(['DeliveryCard-cvaKeyWord', 'relative', 'whitespace-nowrap', 'last:pr-0.4']);
